<script setup lang="ts">
const props = defineProps<{
  backgroundColor?: {
    color: string
  }
  stickerImageColor?: {
    color: string
  }
  stickerTwoImageColor?: {
    color: string
  }
  image: StoryBlokImage
  resolutionScale?: string | number
  stickerImage?: StoryBlokImage
  stickerText?: string
  subtitle?: string
  stickerTwoImage?: StoryBlokImage
  stickerTwoText?: string
  text?: StoryBlokRichText
  title?: string
  type: 'full' | 'image' | 'circle' | 'image-right' | 'universal' | 'image-down'
  disableBreadcrumbs?: boolean
}>()

const backgroundImage = {
  desktop: `url(${props.image.filename.replace('https://a.storyblok.com', 'https://img2.storyblok.com/1530x850')})`,
  mobile: `url(${props.image.filename.replace('https://a.storyblok.com', 'https://img2.storyblok.com/800x375')})`,
}
</script>

<template v-if="type !== 'universal'">
  <div :class="type">
    <div class="bg-wrapper">
      <div class="text-wrapper">
        <AppBreadcrumbs v-if="!disableBreadcrumbs" class="breadcrumbs-wrapper" />
        <h1 class="main-title" v-html="title" />
        <div class="headline-3 mt-2.5 md:mt-5 md:w-max-2/5">
          {{ subtitle }}
        </div>
        <StoryblokRichText class="text" :document="text" />
        <div
          v-if="type !== 'full'"
          :class="{ 'image-circle': (type === 'circle'), 'image-square': (type === 'image'), 'image-right': (type === 'image-right') }"
        >
          <div class="image-wrapper">
            <StoryblokImage v-if="type === 'image-right'" mwidth="182" width="296" :src="image" :resolution-scale="resolutionScale" />
            <StoryblokImage :src="image" height="290" width="590" mheight="380" mwidth="786" :resolution-scale="resolutionScale" />
          </div>
          <div
            v-if="stickerText || stickerImage?.filename"
            class="w-20 h-20 bg-transparent rounded-full absolute z-1 bottom-0 right-0 flex items-center justify-center overflow-hidden border-2px border-white md:w-30 md:h-30 md:bottom-5 xl:w-35 xl:h-35 xl:bottom-10"
            :class="{ 'bottom-18 md:right-25 xl:right-30': stickerTwoText || stickerTwoImage?.filename }"
            :style="{ backgroundColor: stickerImageColor?.color }"
          >
            <StoryblokImage v-if="stickerImage?.filename" :src="stickerImage" width="140" height="140" mheight="80" mwidth="80" />
            <span v-else> {{ stickerText }}</span>
          </div>
          <div
            v-if="stickerTwoText || stickerTwoImage?.filename"
            class="w-20 h-20 bg-transparent rounded-full absolute bottom-0 right-0 flex items-center justify-center overflow-hidden border-2px border-white text-center md:w-30 md:h-30 md:bottom-5 xl:w-35 xl:h-35 xl:bottom-10"
            :style="{ backgroundColor: stickerTwoImageColor?.color }"
          >
            <StoryblokImage v-if="stickerTwoImage?.filename" :src="stickerTwoImage" width="140" height="140" mheight="80" mwidth="80" />
            <span v-else class="md:px-5"> {{ stickerTwoText }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.full {
  @apply flex relative flex-col bg-top bg-no-repeat justify-end md:bg-right;
  background-image: v-bind("backgroundImage.mobile");
  @screen md {
    background-image: v-bind("backgroundImage.desktop");
  }
  .bg-wrapper {
    @apply bg-gradient-to-t md:bg-gradient-to-r;
    --tw-gradient-stops: v-bind("backgroundColor?.color") 50%,
    rgba(255, 255, 255, 0);
    @screen md {
      --tw-gradient-stops: v-bind("backgroundColor?.color") 20%,
      rgba(255, 255, 255, 0);
    }
  }
  .text-wrapper {
    @apply mx-auto p-5 text-white md:pt-5 md:pb-10 md:max-w-1160px xl:px-0;
    .text {
      @apply mt-2.5 md:mt-5 text-white;
    }
  }
  .main-title {
    @apply mt-180px md:mt-10;
  }
}
.image {
  background-color: v-bind("backgroundColor?.color");
  .text-wrapper {
    @apply text-white;
  }
}
.circle {
  @apply bg-gradient;
  .breadcrumbs-wrapper {
    @apply text-white md:text-gray-dark;
  }
}
.image,
.circle {
  @apply flex relative flex-col justify-end md:bg-right;
  .bg-wrapper {
    .text-wrapper {
      h1 {
        @apply md:max-w-5/12;
      }
      @apply mx-auto p-5 relative  md:pt-5 md:pb-10 md:max-w-1160px bg-right bg-no-repeat xl:px-0;
      .breadcrumbs-wrapper {
        @apply absolute top-2.5 left-5 z-50 md:static;
      }
      .text {
        @apply mt-2.5 md:mt-5 md:max-w-5/12;
      }
    }
  }
  .main-title {
    @apply mt-180px md:mt-10;
  }
}
.image-square,
.image-circle {
  .sticker-text {
    @apply hidden text-white shadow-xl absolute bottom-0 right-0 h-140px w-140px rounded-full md:grid place-items-center;
    div {
      @apply text-small h-100px w-100px text-center grid place-items-center;
    }
  }
  .sticker-image {
    @apply absolute shadow-xl overflow-hidden rounded-full h-20 w-20 bottom-0  md:h-140px md:w-140px bg-white grid place-items-center;
  }
}
.image-square {
  @apply absolute top-5 md:top-10 right-5 ml-5 max-w-335px md:max-w-435px lg:max-w-none;
  .sticker-text {
    @apply bg-secondary;
  }
}
.image-circle {
  @apply absolute top-0 right-0 md:right-5 xl:right-0;
  .image-wrapper {
    border-bottom-right-radius: 100% 50px;
    border-bottom-left-radius: 100% 50px;
    @apply max-h-180px overflow-hidden md:max-w-500px md:max-h-none md:rounded-b-full xl:max-w-none;
  }
  .sticker-text {
    @apply bg-primary;
  }
}
.image-right {
  .image-wrapper {
    @apply absolute right-0 -top-0 md:-bottom-10 -z-1 max-w-50 md:max-w-[300px] ;
  }
  .text-wrapper {
    @apply relative overflow-hidden mx-auto p-5 md:pt-5 md:pb-0 md:max-w-1160px xl:px-0;
    .text {
      @apply mt-2.5 md:mt-5;
    }
  }
  .main-title {
    @apply mt-2;
  }
  .headline-3 {
    @apply text-lg font-normal mt-0;
  }
}
</style>
